import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, forkJoin, of } from 'rxjs';

import { ResourceCategory, ResourceKnowledge } from '../../../../Models/resource.models';
import { ResourceApiService } from '../../../../Services/resource-api.service';
import { ResourceManagementApiService } from '../../../../Services/resource-management-api.service';
import { ResourceCatalogKind, ResourceTeamCatalogApiService } from '../../../../Services/resource-team-catalog-api.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';

/** Skill oder Kategorie (Kategorien haben zusätzlich die Abwesenheits-/Kapazitätsoptionen). */
type CatalogItem = ResourceKnowledge & Partial<ResourceCategory>;

/** Herkunft eines Eintrags im gewählten Verwaltungsbereich. */
export type CatalogOrigin = 'global' | 'team' | 'adopted';

interface CatalogEditor {
  id: number | null;
  name: string;
  color: string;
  countsAsAbsence: boolean;
  excludeFromCapacity: boolean;
  sortOrder: number;
}

/**
 * Skill- bzw. Kategorienverwaltung für EINEN Verwaltungsbereich:
 * - teamId null: globaler Katalog (nur globale Einträge),
 * - teamId gesetzt: Einträge dieses Teams – speziell für das Team erstellte und
 *   aus dem globalen Katalog übernommene.
 * Neue Einträge entstehen immer im gewählten Bereich. Die Berechtigung prüft das Backend.
 */
@Component({
  selector: 'app-resource-catalog',
  templateUrl: './resource-catalog.component.html',
  styleUrls: ['./resource-catalog.component.css']
})
export class ResourceCatalogComponent implements OnChanges {
  @Input() kind: ResourceCatalogKind = 'skills';
  /** null = globaler Katalog. */
  @Input() teamId: number | null = null;
  @Input() teamName = '';
  /** Darf im gewählten Bereich angelegt, bearbeitet und gelöscht werden. */
  @Input() canEdit = false;

  items: CatalogItem[] = [];
  teamItemIds = new Set<number>();
  loading = false;
  saving = false;
  error = '';

  searchText = '';
  originFilter: 'all' | 'team' | 'adopted' = 'all';

  editor: CatalogEditor | null = null;

  adoptOpen = false;
  adoptSearch = '';
  adoptSelection = new Set<number>();

  private loadSequence = 0;

  constructor(
    private resourceApi: ResourceApiService,
    private managementApi: ResourceManagementApiService,
    private catalogApi: ResourceTeamCatalogApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kind'] || changes['teamId']) {
      this.searchText = '';
      this.originFilter = 'all';
      this.editor = null;
      this.adoptOpen = false;
      this.load();
    }
  }

  // ---- Bezeichnungen ---------------------------------------------------------

  get isSkills(): boolean { return this.kind === 'skills'; }
  get singular(): string { return this.isSkills ? 'Skill' : 'Kategorie'; }
  get plural(): string { return this.isSkills ? 'Skills' : 'Kategorien'; }
  get teamMode(): boolean { return this.teamId !== null; }
  get scopeLabel(): string { return this.teamMode ? `Team ${this.teamName}` : 'Globaler Katalog'; }

  // ---- Anzeige -----------------------------------------------------------------

  origin(item: CatalogItem): CatalogOrigin {
    if (!item.ownerTeamId) {
      return this.teamMode ? 'adopted' : 'global';
    }
    return 'team';
  }

  originLabel(item: CatalogItem): string {
    switch (this.origin(item)) {
      case 'team': return 'Nur dieses Team';
      case 'adopted': return 'Aus globalem Katalog';
      default: return 'Global';
    }
  }

  /** Einträge des gewählten Bereichs (ohne Such-/Herkunftsfilter). */
  get scopeItems(): CatalogItem[] {
    return this.teamMode
      ? this.items.filter(item => this.teamItemIds.has(item.id))
      : this.items.filter(item => !item.ownerTeamId);
  }

  get visibleItems(): CatalogItem[] {
    const search = this.searchText.trim().toLocaleLowerCase('de');
    return this.scopeItems.filter(item =>
      (!search || item.name.toLocaleLowerCase('de').includes(search)) &&
      (this.originFilter === 'all' || this.origin(item) === this.originFilter));
  }

  get adoptableItems(): CatalogItem[] {
    const search = this.adoptSearch.trim().toLocaleLowerCase('de');
    return this.items.filter(item =>
      !item.ownerTeamId &&
      !this.teamItemIds.has(item.id) &&
      (!search || item.name.toLocaleLowerCase('de').includes(search)));
  }

  /** In der Teamplanung gilt ohne Teamkategorien der globale Katalog (Hinweis für Kategorien). */
  get showGlobalFallbackHint(): boolean {
    return this.teamMode && !this.isSkills && !this.loading && this.scopeItems.length === 0;
  }

  canEditItem(item: CatalogItem): boolean {
    return this.canEdit && this.origin(item) !== 'adopted';
  }

  trackItem(_index: number, item: CatalogItem): number {
    return item.id;
  }

  // ---- Laden ---------------------------------------------------------------------

  load(): void {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.error = '';

    const items$: Observable<CatalogItem[]> = this.isSkills
      ? this.resourceApi.getKnowledge()
      : this.resourceApi.getCategories();
    const teamIds$: Observable<number[]> = this.teamId !== null
      ? this.catalogApi.get(this.teamId, this.kind)
      : of([]);

    forkJoin({ items: items$, teamIds: teamIds$ }).subscribe({
      next: result => {
        if (sequence !== this.loadSequence) return;
        this.items = [...result.items].sort((a, b) => this.isSkills
          ? a.name.localeCompare(b.name, 'de')
          : ((a.sortOrder ?? 0) - (b.sortOrder ?? 0)) || a.name.localeCompare(b.name, 'de'));
        this.teamItemIds = new Set(result.teamIds);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        if (sequence !== this.loadSequence) return;
        this.loading = false;
        this.error = this.errorText(err, `${this.plural} konnten nicht geladen werden.`);
      }
    });
  }

  // ---- Anlegen / Bearbeiten ----------------------------------------------------------

  openCreate(): void {
    if (!this.canEdit) return;
    this.editor = {
      id: null,
      name: '',
      color: this.isSkills ? '#607d8b' : '#2563eb',
      countsAsAbsence: false,
      excludeFromCapacity: false,
      sortOrder: 0
    };
  }

  openEdit(item: CatalogItem): void {
    if (!this.canEditItem(item)) return;
    this.editor = {
      id: item.id,
      name: item.name,
      color: item.color || (this.isSkills ? '#607d8b' : '#2563eb'),
      countsAsAbsence: !!item.countsAsAbsence,
      excludeFromCapacity: !!item.excludeFromCapacity,
      sortOrder: item.sortOrder ?? 0
    };
  }

  closeEditor(): void {
    if (!this.saving) this.editor = null;
  }

  save(): void {
    const editor = this.editor;
    if (!editor || this.saving) return;
    const name = editor.name.trim();
    if (!name) return;

    // Kurze Vorabprüfung im gewählten Bereich; verbindlich prüft das Backend.
    const duplicate = this.scopeItems.some(item =>
      item.id !== editor.id && item.name.toLocaleLowerCase('de') === name.toLocaleLowerCase('de'));
    if (duplicate) {
      this.notify(`In diesem Bereich gibt es bereits einen Eintrag „${name}“.`);
      return;
    }

    const teamId = editor.id === null ? this.teamId : undefined;
    const request: Observable<unknown> = this.isSkills
      ? (editor.id === null
        ? this.managementApi.createSkill({ name, color: editor.color, teamId })
        : this.managementApi.updateSkill(editor.id, { name, color: editor.color }))
      : (editor.id === null
        ? this.managementApi.createCategory({
          name, color: editor.color, countsAsAbsence: editor.countsAsAbsence,
          excludeFromCapacity: editor.excludeFromCapacity, sortOrder: 0, teamId
        })
        : this.managementApi.updateCategory(editor.id, {
          name, color: editor.color, countsAsAbsence: editor.countsAsAbsence,
          excludeFromCapacity: editor.excludeFromCapacity, sortOrder: editor.sortOrder
        }));

    this.saving = true;
    request.subscribe({
      next: () => {
        this.saving = false;
        this.editor = null;
        this.notify(`${this.singular} gespeichert.`);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.notify(this.errorText(err, `${this.singular} konnte nicht gespeichert werden.`));
      }
    });
  }

  // ---- Löschen / aus Team entfernen ---------------------------------------------------

  remove(item: CatalogItem): void {
    if (!this.canEditItem(item) || this.saving) return;
    const dependency = this.isSkills
      ? 'Ein Skill kann nur gelöscht werden, solange er keinem Mitarbeiter zugeordnet ist.'
      : 'Eine Kategorie kann nur gelöscht werden, solange kein Termin sie verwendet.';
    this.confirm({
      title: `${this.singular} löschen`,
      message: `„${item.name}“ wirklich endgültig löschen?`,
      details: [dependency],
      confirmText: 'Löschen'
    }, () => {
      this.saving = true;
      const request = this.isSkills
        ? this.managementApi.deleteSkill(item.id)
        : this.managementApi.deleteCategory(item.id);
      request.subscribe({
        next: () => {
          this.saving = false;
          this.notify(`${this.singular} gelöscht.`);
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.saving = false;
          this.notify(this.errorText(err, `${this.singular} konnte nicht gelöscht werden.`));
        }
      });
    });
  }

  /** Nimmt einen übernommenen globalen Eintrag wieder aus dem Team (global bleibt er bestehen). */
  removeFromTeam(item: CatalogItem): void {
    if (!this.canEdit || this.teamId === null || this.origin(item) !== 'adopted' || this.saving) return;
    this.confirm({
      title: 'Aus dem Team entfernen',
      message: `„${item.name}“ aus dem Katalog von ${this.teamName} entfernen?`,
      details: ['Der globale Eintrag und andere Teams bleiben unverändert.'],
      confirmText: 'Entfernen'
    }, () => this.saveAdopted(this.adoptedIds().filter(id => id !== item.id), 'Aus dem Team entfernt.'));
  }

  // ---- Aus globalem Katalog übernehmen ----------------------------------------------

  openAdopt(): void {
    if (!this.canEdit || this.teamId === null) return;
    this.adoptSearch = '';
    this.adoptSelection = new Set<number>();
    this.adoptOpen = true;
  }

  closeAdopt(): void {
    if (!this.saving) this.adoptOpen = false;
  }

  toggleAdopt(id: number, checked: boolean): void {
    const next = new Set(this.adoptSelection);
    if (checked) next.add(id); else next.delete(id);
    this.adoptSelection = next;
  }

  saveAdopt(): void {
    if (this.adoptSelection.size === 0) return;
    const ids = Array.from(new Set([...this.adoptedIds(), ...this.adoptSelection]));
    this.saveAdopted(ids, `${this.adoptSelection.size} ${this.adoptSelection.size === 1 ? this.singular : this.plural} übernommen.`,
      () => this.adoptOpen = false);
  }

  private adoptedIds(): number[] {
    return this.scopeItems.filter(item => this.origin(item) === 'adopted').map(item => item.id);
  }

  private saveAdopted(ids: number[], message: string, done?: () => void): void {
    if (this.teamId === null) return;
    this.saving = true;
    this.catalogApi.save(this.teamId, this.kind, ids).subscribe({
      next: () => {
        this.saving = false;
        done?.();
        this.notify(message);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.notify(this.errorText(err, 'Die Teamzuordnung konnte nicht gespeichert werden.'));
      }
    });
  }

  // ---- Hilfen ---------------------------------------------------------------------------

  private confirm(data: ConfirmDialogData, onConfirm: () => void): void {
    this.dialog.open(ConfirmDialogComponent, { data, width: '440px', maxWidth: '95vw' })
      .afterClosed()
      .subscribe(confirmed => { if (confirmed) onConfirm(); });
  }

  private errorText(error: HttpErrorResponse, fallback: string): string {
    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }
    if (error.status === 403 || error.status === 404) {
      return `Keine Berechtigung für diesen Bereich.`;
    }
    if (error.status === 0) {
      return 'Server nicht erreichbar.';
    }
    return fallback;
  }

  private notify(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 4000 });
  }
}
